import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CalendarClock, SkipForward, CheckCircle2, Image as ImageIcon, FileText } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const SRC_LABEL = {
  exif_metadata: "EXIF / Metadata", visible_in_image: "Visible in Image",
  document_content: "Document Text", user_entered: "User Entered", user_confirmed: "User Confirmed",
};

export default function BulkDateConfirm({ open, onClose, matterId, onChanged }) {
  const [items, setItems] = useState([]);
  const [assign, setAssign] = useState({});      // id -> date string
  const [selected, setSelected] = useState({});  // id -> bool (for same-date bulk)
  const [sharedDate, setSharedDate] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    try {
      const { data } = await api.get(`/evidence/date-queue${matterId ? `?matter_id=${matterId}` : ""}`);
      setItems(data.items || []);
      setAssign({}); setSelected({}); setSharedDate("");
    } catch (e) { console.error("date-queue load failed", e); }
  }, [open, matterId]);
  useEffect(() => { load(); }, [load]);

  const setDate = (id, val) => setAssign((p) => ({ ...p, [id]: val }));
  const skip = (id) => setItems((p) => p.filter((i) => i.id !== id));

  // "Confirm all detected" allowed only when every visible item has exactly ONE detected date and no conflict.
  const allSingle = items.length > 0 && items.every((i) => (i.detected_dates || []).length === 1 && !i.date_review_needed);
  const confirmAllDetected = () => {
    const next = { ...assign };
    items.forEach((i) => { if ((i.detected_dates || []).length === 1) next[i.id] = i.detected_dates[0].value; });
    setAssign(next);
    toast.success("Detected dates filled in — review, then Save");
  };

  const applyShared = () => {
    if (!sharedDate) { toast.error("Pick a date to apply"); return; }
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) { toast.error("Select items first"); return; }
    setAssign((p) => { const n = { ...p }; ids.forEach((id) => { n[id] = sharedDate; }); return n; });
    toast.success(`Applied to ${ids.length} item(s)`);
  };

  const save = async () => {
    const assignments = Object.entries(assign).filter(([, v]) => v).map(([id, event_date]) => ({ id, event_date }));
    if (!assignments.length) { toast.error("Assign at least one date"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/evidence/bulk-confirm-date", { assignments });
      toast.success(`Confirmed ${data.confirmed} date(s)`);
      const done = new Set(assignments.map((a) => a.id));
      setItems((p) => p.filter((i) => !done.has(i.id)));
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const assignedCount = Object.values(assign).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="bulk-date-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-medium tracking-tight flex items-center gap-2">
            <CalendarClock className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> Confirm event dates
          </DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
            {items.length} item(s) need a confirmed event date. Your originally detected dates and file metadata are always preserved.
          </DialogDescription>
        </DialogHeader>

        {items.length === 0 ? (
          <p className="text-sm flex items-center gap-2 py-6" style={{ color: "var(--ek-text-muted)" }} data-testid="date-queue-empty">
            <CheckCircle2 className="h-4 w-4" style={{ color: "var(--ek-secure)" }} /> Every item has a confirmed event date.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap rounded-md border p-2" style={{ borderColor: "var(--ek-border)" }}>
              <Button variant="outline" onClick={confirmAllDetected} disabled={!allSingle} data-testid="confirm-all-detected-btn"
                className="rounded-md text-xs" style={{ borderColor: "var(--ek-border)", background: "transparent", opacity: allSingle ? 1 : 0.5 }}>
                Confirm all detected {allSingle ? "" : "(single-date only)"}
              </Button>
              <span className="h-4 w-px" style={{ background: "var(--ek-border)" }} />
              <input type="date" value={sharedDate} onChange={(e) => setSharedDate(e.target.value)} data-testid="shared-date-input"
                className="rounded-md border px-2 py-1 text-sm bg-transparent" style={{ borderColor: "var(--ek-border)", color: "var(--ek-text)" }} />
              <Button variant="outline" onClick={applyShared} data-testid="apply-shared-btn" className="rounded-md text-xs" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
                Apply to selected
              </Button>
            </div>

            <div className="max-h-[48vh] overflow-y-auto space-y-2 pr-1 mt-2">
              {items.map((it) => {
                const Icon = it.format === "photo" ? ImageIcon : FileText;
                const dd = it.detected_dates || [];
                return (
                  <div key={it.id} className="border rounded-md p-3" style={{ borderColor: it.date_review_needed ? "rgba(240,97,109,0.4)" : "var(--ek-border)" }} data-testid={`date-item-${it.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <input type="checkbox" checked={!!selected[it.id]} onChange={(e) => setSelected((p) => ({ ...p, [it.id]: e.target.checked }))} data-testid={`date-select-${it.id}`} />
                      <Icon className="h-4 w-4 shrink-0" style={{ color: "var(--ek-text-muted)" }} />
                      <span className="text-sm truncate" data-private>{it.filename}</span>
                      {it.date_review_needed && <span className="ek-mono-label" style={{ color: "var(--ek-error)" }}>REVIEW: conflicting dates</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {dd.length === 0 && <span className="text-xs" style={{ color: "var(--ek-text-muted)" }}>No date detected — enter one:</span>}
                      {dd.map((d, i) => {
                        const active = assign[it.id] === d.value;
                        return (
                          <button key={i} type="button" data-testid={`date-pick-${it.id}-${i}`} onClick={() => setDate(it.id, d.value)}
                            className="rounded-md border px-2 py-1 text-xs" style={{ borderColor: active ? "var(--ek-primary)" : "var(--ek-border)", background: active ? "rgba(46,130,246,0.1)" : "transparent", color: "var(--ek-text)" }}>
                            {String(d.value).slice(0, 10)} <span style={{ color: "var(--ek-text-muted)" }}>· {SRC_LABEL[d.source] || d.source}</span>
                          </button>
                        );
                      })}
                      <input type="date" value={assign[it.id] && !dd.some((d) => d.value === assign[it.id]) ? assign[it.id] : ""} onChange={(e) => setDate(it.id, e.target.value)}
                        data-testid={`date-manual-${it.id}`} className="rounded-md border px-2 py-1 text-xs bg-transparent" style={{ borderColor: "var(--ek-border)", color: "var(--ek-text)" }} />
                      <button type="button" onClick={() => skip(it.id)} data-testid={`date-skip-${it.id}`} className="text-xs inline-flex items-center gap-1 ml-auto" style={{ color: "var(--ek-text-muted)" }}>
                        <SkipForward className="h-3 w-3" /> Review later
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between mt-2">
              <span className="text-xs" style={{ color: "var(--ek-text-muted)" }}>{assignedCount} assigned</span>
              <Button onClick={save} disabled={busy || !assignedCount} data-testid="bulk-date-save-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
                {busy ? "Saving…" : `Save ${assignedCount} confirmation(s)`}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
